import type {
  FinishWorkoutOptions,
  FinishWorkoutResult,
  WorkoutSet,
} from "../../../shared/src";
import { ApiError } from "../api-client";
import { enqueue, peekAll, removeSucceeded, markFailed } from "./queue";

function isOnline(): boolean {
  if (typeof navigator === "undefined") return true;
  return navigator.onLine !== false;
}

function isNetworkFailure(err: unknown): boolean {
  if (err instanceof ApiError) return false;
  if (!isOnline()) return true;
  return err instanceof TypeError;
}

export async function withQueueAddWorkoutSet(
  exec: () => Promise<WorkoutSet>,
  args: {
    workout_id: number;
    exercise_id: number;
    weight_kg: number;
    reps: number;
  },
): Promise<WorkoutSet> {
  if (isOnline()) {
    try {
      return await exec();
    } catch (err) {
      if (!isNetworkFailure(err)) throw err;
    }
  }
  const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  await enqueue({
    kind: "addWorkoutSet",
    workout_id: args.workout_id,
    input: {
      exercise_id: args.exercise_id,
      weight_kg: args.weight_kg,
      reps: args.reps,
    },
    temp_id: tempId,
    created_at: new Date().toISOString(),
  });
  return {
    id: -1,
    workout_id: args.workout_id,
    exercise_id: args.exercise_id,
    set_index: 0,
    weight_kg: args.weight_kg,
    reps: args.reps,
    completed_at: new Date().toISOString(),
  };
}

export async function withQueueUpdateWorkoutSet(
  exec: () => Promise<WorkoutSet>,
  args: { set_id: number; patch: { weight_kg?: number; reps?: number } },
): Promise<WorkoutSet> {
  if (isOnline()) {
    try {
      return await exec();
    } catch (err) {
      if (!isNetworkFailure(err)) throw err;
    }
  }
  await enqueue({
    kind: "updateWorkoutSet",
    set_id: args.set_id,
    patch: args.patch,
    created_at: new Date().toISOString(),
  });
  return {
    id: args.set_id,
    workout_id: -1,
    exercise_id: -1,
    set_index: 0,
    weight_kg: args.patch.weight_kg ?? null,
    reps: args.patch.reps ?? null,
    completed_at: new Date().toISOString(),
  };
}

export async function withQueueDeleteWorkoutSet(
  exec: () => Promise<void>,
  id: number,
): Promise<void> {
  if (isOnline()) {
    try {
      await exec();
      return;
    } catch (err) {
      if (!isNetworkFailure(err)) throw err;
    }
  }
  await enqueue({
    kind: "deleteWorkoutSet",
    set_id: id,
    created_at: new Date().toISOString(),
  });
}

export async function withQueueFinishWorkout(
  exec: () => Promise<FinishWorkoutResult>,
  workoutId: number,
  opts?: FinishWorkoutOptions,
): Promise<FinishWorkoutResult> {
  if (isOnline()) {
    try {
      return await exec();
    } catch (err) {
      if (!isNetworkFailure(err)) throw err;
    }
  }
  const hasOpts =
    opts !== undefined &&
    (opts.started_at !== undefined || opts.ended_at !== undefined);
  await enqueue({
    kind: "finishWorkout",
    workout_id: workoutId,
    ...(hasOpts ? { opts } : {}),
    created_at: new Date().toISOString(),
  });
  // Offline path: bumps are computed server-side on drain. The user won't
  // see a toast for this workout, but the program will still bump correctly
  // when the queue replays.
  return {
    workout: {
      id: workoutId,
      training_day_id: null,
      started_at: new Date().toISOString(),
      ended_at: new Date().toISOString(),
      notes: null,
    },
    bumps: [],
  };
}

export interface DrainResult {
  succeeded: number;
  failedAt: { id: number; reason: string } | null;
}

export async function drainQueue(): Promise<DrainResult> {
  const { _rawForReplay } = await import("../api-client");
  let succeeded = 0;
  const entries = await peekAll();
  for (const entry of entries) {
    try {
      await replay(entry.mutation, _rawForReplay);
      await removeSucceeded(entry.id);
      succeeded++;
    } catch (err) {
      const reason = err instanceof Error ? err.message : "unknown error";
      await markFailed(entry.id, reason);
      return { succeeded, failedAt: { id: entry.id, reason } };
    }
  }
  return { succeeded, failedAt: null };
}

async function replay(
  m: import("../../../shared/src").QueuedMutation,
  raw: typeof import("../api-client")["_rawForReplay"],
): Promise<void> {
  switch (m.kind) {
    case "addWorkoutSet":
      await raw.addWorkoutSet(m.workout_id, m.input);
      return;
    case "updateWorkoutSet":
      await raw.updateWorkoutSet(m.set_id, m.patch);
      return;
    case "deleteWorkoutSet":
      await raw.deleteWorkoutSet(m.set_id);
      return;
    case "finishWorkout":
      await raw.finishWorkout(m.workout_id, m.opts);
      return;
  }
}
